import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import {
  Building2, Users, Wallet, AlertCircle, ArrowUpRight, TrendingUp,
  Sparkles, CheckCircle2, Clock, ArrowRight, Zap, FileText, IdCard, FileSignature, CalendarClock, ShieldCheck,
  Settings2, GripVertical, Eye, EyeOff, Plus, Trash2, Circle, Check, X, PiggyBank, Loader2
} from "lucide-react";
import { toast } from "sonner";
import RevenueBlock from "../../components/dashboard/RevenueBlock"; // eslint-disable-line no-unused-vars
import GettingStarted from "../../components/dashboard/GettingStarted";
import { OnboardingBanner } from "../../components/onboarding/OnboardingBanner";
import { OnboardingChecklist } from "../../components/onboarding/OnboardingChecklist";
import { GoFurtherChecklist } from "../../components/guides/GoFurtherChecklist";
import { openGuidedProperty } from "../../components/onboarding/EmptyState";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AlertContextMenu, TaskFromAlertModal, TaskEditModal, TASK_STATUSES } from "../../components/dashboard/AlertActions";
import { BirthdayReminders } from "../../components/dashboard/BirthdayReminders";
import AssistantIA from "./AssistantIA";

const BLOCK_LABELS = {
  onboarding: "Bien démarrer",
  further: "Aller plus loin",
  stats: "Statistiques",
  assistant: "Assistant IA",
  tasks: "Tâches à venir",
  alerts: "Alertes & rappels",
  properties: "Aperçu des biens",
};
const DEFAULT_ORDER = ["onboarding", "further", "stats", "assistant", "tasks", "alerts", "properties"];

function StatCard({ icon: Icon, label, value, trend, warn, testid }) {
  return (
    <div className="bg-brand-graphite rounded-[10px] p-5 lg:p-7 text-brand-ivoire min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <Icon className="w-5 h-5 text-brand-laiton" strokeWidth={1.5} />
        {warn && <span className="text-[11px] font-medium tracking-[0.04em] text-[#e08f74]">Attention</span>}
        {trend && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-laiton">
            <TrendingUp className="w-3.5 h-3.5" /> {trend}
          </span>
        )}
      </div>
      <p className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-[-0.02em] text-brand-laiton whitespace-nowrap" data-testid={`${testid}-value`}>{value}</p>
      <p className="text-sm text-[#a8a398] mt-3">{label}</p>
    </div>
  );
}

function TaskMeta({ t, onStatus, onInsurance, onPiece, busy }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1">
      {t.assignee && <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-softer text-brand-green/70" data-testid={`task-assignee-${t.id}`}>{t.assignee}</span>}
      {t.due && <span className="text-[11px] text-brand-green/50">{/^\d{4}-\d{2}-\d{2}$/.test(t.due) ? `Fin attendue ${new Date(t.due).toLocaleDateString("fr-FR")}` : t.due}</span>}
      {(t.source_key || t.status) && (
        <select value={t.status || "À faire"} onChange={(e) => onStatus(t, e.target.value)} className="text-[11px] px-2 py-0.5 rounded-full border border-brand-soft bg-white text-brand-greenDeep" data-testid={`task-status-${t.id}`}>
          {TASK_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      )}
      {t.kind === "assurance" && !t.done && t.insurance_requested_at && (
        t.insurance_received
          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700" data-testid={`task-insurance-received-${t.id}`}>Attestation reçue</span>
          : (Date.now() - new Date(t.insurance_relance_at || t.insurance_requested_at).getTime()) >= 15 * 86400000
            ? <button onClick={() => onInsurance(t, true)} disabled={busy} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 disabled:opacity-60" data-testid={`task-insurance-relance-${t.id}`}>Relancer le locataire</button>
            : <span className="text-[11px] text-brand-green/45" data-testid={`task-insurance-wait-${t.id}`}>Relance possible le {new Date(new Date(t.insurance_relance_at || t.insurance_requested_at).getTime() + 15 * 86400000).toLocaleDateString("fr-FR")}</span>
      )}
      {t.kind === "assurance" && !t.done && !t.insurance_requested_at && (
        <button onClick={() => onInsurance(t)} disabled={busy} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep disabled:opacity-60 inline-flex items-center gap-1" data-testid={`task-insurance-mail-${t.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Demander l'attestation par e-mail
        </button>
      )}
      {t.kind === "company_piece" && !t.done && t.tenant_id && (() => {
        const last = t.piece_relance_at || t.piece_requested_at;
        const canRelance = last && (Date.now() - new Date(last).getTime()) >= 15 * 86400000;
        if (last && !canRelance) return <span className="text-[11px] text-brand-green/45">Relance possible le {new Date(new Date(last).getTime() + 15 * 86400000).toLocaleDateString("fr-FR")}</span>;
        return <button onClick={() => onPiece(t, !!last)} disabled={busy} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep disabled:opacity-60" data-testid={`task-piece-mail-${t.id}`}>{last ? "Relancer le locataire" : "Demander le document par e-mail"}</button>;
      })()}
      {t.last_action && <span className="text-[11px] text-brand-emerald">{t.last_action}</span>}
    </div>
  );
}

function TasksBlock({ version }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => {});
  const loadBirthdays = () => api.get("/dashboard/birthdays").then((r) => setBirthdays(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadBirthdays(); }, [version]);

  const setStatus = async (t, status) => {
    setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, status, done: status === "Terminé" } : x));
    try { await api.put(`/tasks/${t.id}`, { status }); } catch { load(); }
  };
  const askPiece = async (t, relance) => {
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/tenants/${t.tenant_id}/request-piece?kind=${t.piece_kind}${relance ? "&relance=true" : ""}`);
      toast.success(data.sent ? `Demande envoyée à ${data.to}` : `Demande préparée pour ${data.to} (e-mail non parti)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); } finally { setBusyId(null); }
  };
  const askInsurance = async (t, relance = false) => {
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/tasks/${t.id}/send-insurance-request${relance ? "?relance=true" : ""}`);
      toast.success(data.sent ? `${relance ? "Relance" : "Demande"} envoyée à ${data.to}` : `${relance ? "Relance" : "Demande"} préparée pour ${data.to} (e-mail non parti)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusyId(null); }
  };

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data } = await api.post("/tasks", { title: t });
      setTasks((prev) => [...prev, data]);
      setTitle("");
    } catch (e) { toast.error("Échec de l'ajout"); }
    finally { setAdding(false); }
  };

  const toggle = async (id) => {
    setTasks((prev) => prev.map((x) => x.id === id ? { ...x, done: !x.done, status: !x.done ? "Terminé" : "À faire" } : x));
    try { await api.put(`/tasks/${id}/toggle`); } catch (e) { load(); }
  };

  const remove = async (id) => {
    setTasks((prev) => prev.filter((x) => x.id !== id));
    try { await api.delete(`/tasks/${id}`); } catch (e) { toast.error("Échec de la suppression"); load(); }
  };

  const [showArchived, setShowArchived] = useState(false);
  const archived = tasks.filter((t) => t.done);
  const ordered = showArchived ? archived : tasks.filter((t) => !t.done);
  const openCount = tasks.length - archived.length;

  return (
    <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="tasks-block">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg text-brand-greenDeep">Tâches à venir</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-brand-green/50">{openCount} en cours</span>
          <button onClick={() => setShowArchived((v) => !v)} className={`text-xs font-semibold px-3 py-1 rounded-full border ${showArchived ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "border-brand-soft text-brand-greenDeep hover:bg-brand-softer"}`} data-testid="tasks-archive-toggle">{showArchived ? "← Tâches actives" : `Tâches archivées (${archived.length})`}</button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="Ajouter une tâche…"
          className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-brand-soft text-sm outline-none focus:border-brand-lime"
          data-testid="task-input"
        />
        <button onClick={add} disabled={adding} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-medium disabled:opacity-60" data-testid="task-add-btn">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>
      {editing && <TaskEditModal task={editing} onClose={() => setEditing(null)} onSaved={(d) => { setEditing(null); setTasks((prev) => prev.map((x) => x.id === d.id ? d : x)); }} />}
      <BirthdayReminders items={birthdays} sender={user?.name || ""} onChange={loadBirthdays} />
      <div className="space-y-1">
        {showArchived && archived.length > 0 && <p className="text-[11px] text-brand-green/45 pb-1">Cliquez sur une tâche pour la modifier ; repassez-la en « À faire » ou « En cours » pour la réactiver.</p>}
        {ordered.length === 0 && <p className="text-sm text-brand-green/45 py-2" data-testid="tasks-empty">{showArchived ? "Aucune tâche archivée." : "Aucune tâche. Ajoutez-en une ci-dessus."}</p>}
        {ordered.map((t) => (
          <div key={t.id} className="group flex items-start gap-3 py-2 px-2 -mx-2 rounded-xl hover:bg-brand-softer transition-colors" data-testid={`task-row-${t.id}`}>
            <button onClick={() => toggle(t.id)} className="mt-0.5 flex-shrink-0" data-testid={`task-toggle-${t.id}`}>
              {t.done
                ? <CheckCircle2 className="w-5 h-5 text-brand-limeDark" />
                : <Circle className="w-5 h-5 text-brand-green/30 hover:text-brand-limeDark" />}
            </button>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={(e) => { if (!e.target.closest("button,select")) setEditing(t); }} title="Cliquer pour modifier" data-testid={`task-open-${t.id}`}>
              <p className={`text-sm font-medium ${t.done ? "line-through text-brand-green/40" : "text-brand-green"}`}>{t.title}</p>
              {t.source_key || t.assignee || t.status ? <TaskMeta t={t} onStatus={setStatus} onInsurance={askInsurance} onPiece={askPiece} busy={busyId === t.id} /> : (t.due && <p className="text-xs text-brand-green/50">{t.due}</p>)}
            </div>
            <button onClick={() => remove(t.id)} className="flex-shrink-0 text-brand-green/25 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`task-delete-${t.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DraggableBlock({ blockKey, hidden, onToggle, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={blockKey} dragListener={false} dragControls={controls} className="bg-brand-softer/40 rounded-2xl p-2">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-brand-green/40 hover:text-brand-greenDeep touch-none p-1" data-testid={`drag-${blockKey}`}>
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-brand-greenDeep">{BLOCK_LABELS[blockKey]}</span>
        </div>
        <button onClick={onToggle} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${hidden ? "bg-brand-soft text-brand-green/60" : "bg-brand-lime/30 text-brand-greenDeep"}`} data-testid={`toggle-${blockKey}`}>
          {hidden ? <><EyeOff className="w-3.5 h-3.5" /> Masqué</> : <><Eye className="w-3.5 h-3.5" /> Affiché</>}
        </button>
      </div>
      <div className={hidden ? "opacity-40 pointer-events-none" : ""}>{children}</div>
    </Reorder.Item>
  );
}

function OnboardingModal({ onFinish }) {
  const [sel, setSel] = useState(() => {
    const s = {};
    DEFAULT_ORDER.forEach((k) => { s[k] = k !== "assistant"; });
    return s;
  });
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setSel((p) => ({ ...p, [k]: !p[k] }));
  const submit = async () => {
    setBusy(true);
    await onFinish(DEFAULT_ORDER.filter((k) => !sel[k]));
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="dash-onboarding-modal">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
        <div className="bg-brand-greenDeep text-white p-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-xs font-semibold mb-3"><Sparkles className="w-3.5 h-3.5" /> Bienvenue</div>
          <h3 className="font-display text-2xl font-semibold">Composez votre tableau de bord</h3>
          <p className="text-sm text-white/70 mt-1.5">Choisissez les sections à afficher. Vous pourrez les réorganiser ou les modifier à tout moment via « Personnaliser ».</p>
        </div>
        <div className="p-6 space-y-2">
          {DEFAULT_ORDER.map((k) => (
            <button key={k} onClick={() => toggle(k)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${sel[k] ? "bg-brand-lime/15 border-brand-limeDark/40" : "border-brand-soft"}`} data-testid={`onboarding-toggle-${k}`}>
              <span className="text-sm font-medium text-brand-greenDeep">{BLOCK_LABELS[k]}</span>
              <span className={`w-11 h-6 rounded-full relative transition-colors ${sel[k] ? "bg-brand-limeDark" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${sel[k] ? "left-[22px]" : "left-0.5"}`} /></span>
            </button>
          ))}
        </div>
        <div className="p-6 pt-0">
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm disabled:opacity-60" data-testid="onboarding-finish-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Afficher mon tableau de bord
          </button>
        </div>
      </div>
    </div>
  );
}


export default function DashboardHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [folded, setFolded] = useState(() => { try { return JSON.parse(localStorage.getItem("dash_alerts_folded") || "[]"); } catch { return []; } });
  const [hidden, setHidden] = useState(["assistant"]);
  const [editMode, setEditMode] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [disputes, setDisputes] = useState({ count: 0, items: [] });
  const [properties, setProperties] = useState([]); // eslint-disable-line no-unused-vars
  const [depositBusy, setDepositBusy] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [taskAlert, setTaskAlert] = useState(null);
  const [tasksVersion, setTasksVersion] = useState(0);
  const [view, setView] = useState("dashboard");
  const openCtx = (e, alert) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, alert }); };
  const dismissAlert = async (alert) => {
    try { await api.post("/dashboard/alerts/dismiss", { key: alert.key }); await reloadAlerts(); toast.success("Information supprimée"); }
    catch { toast.error("Erreur"); }
  };
  const onTaskCreated = async () => { setTaskAlert(null); setTasksVersion((v) => v + 1); await reloadAlerts(); };
  const reloadAlerts = () => api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  const markDepositPaid = async (id, e) => {
    e.stopPropagation();
    setDepositBusy(id);
    try {
      await api.put(`/transactions/${id}/status`, { status: "Payé" });
      await reloadAlerts();
      toast.success("Dépôt marqué encaissé");
    } catch { toast.error("Impossible de marquer encaissé"); }
    finally { setDepositBusy(null); }
  };
  const dismissDoc = (d, e) => { e.stopPropagation(); dismissAlert(d); };

  useEffect(() => {
    const nc = { params: { _: Date.now() } };
    api.get("/dashboard", nc).then((r) => setData(r.data)).catch(() => {});
    api.get("/alerts", nc).then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/properties", nc).then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/edl/disputes/open-count", nc).then((r) => setDisputes(r.data)).catch(() => {});
    api.get("/dashboard/preferences").then((r) => {
      if (r.data?.order?.length) setOrder(r.data.order);
      setHidden(r.data?.hidden || []);
      if (r.data && r.data.configured === false) {
        api.get("/onboarding/state").then((o) => {
          if (o.data?.eligible && !o.data?.has_lease) api.put("/dashboard/preferences", { order: r.data.order || DEFAULT_ORDER, hidden: ["assistant"] }).then(() => setHidden(["assistant"])).catch(() => {});
          else setShowOnboarding(true);
        }).catch(() => setShowOnboarding(true));
      }
    }).catch(() => {});
  }, []);

  const finishOnboarding = async (chosenHidden) => {
    setHidden(chosenHidden);
    setShowOnboarding(false);
    try { await api.put("/dashboard/preferences", { order, hidden: chosenHidden }); }
    catch { /* silencieux : les préférences seront re-tentées à la prochaine sauvegarde */ }
  };

  const toggleHidden = (key) =>
    setHidden((h) => h.includes(key) ? h.filter((x) => x !== key) : [...h, key]);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put("/dashboard/preferences", { order, hidden });
      toast.success("Tableau de bord personnalisé enregistré");
      setEditMode(false);
    } catch (e) { toast.error("Échec de l'enregistrement"); }
    finally { setSavingPrefs(false); }
  };

  if (!data) {
    return <div className="py-20 text-center text-brand-green/50">Chargement...</div>;
  }

  const { stats, propertiesPreview } = data;
  const hasAlerts = alerts && (alerts.passoire.length > 0 || alerts.expiring_docs.length > 0 || (alerts.expiring_pieces || []).length > 0 || (alerts.ending_leases || []).length > 0 || (alerts.revision_reminders || []).length > 0 || (alerts.ending_loans || []).length > 0 || (alerts.loan_insurance_reviews || []).length > 0 || (alerts.loan_payment_reminders || []).length > 0 || (alerts.unpaid_deposits || []).length > 0);
  const visibleDocs = alerts?.expiring_docs || [];

  const hideBlock = (key) => {
    const next = hidden.includes(key) ? hidden : [...hidden, key];
    setHidden(next);
    api.put("/dashboard/preferences", { order, hidden: next }).then(() => toast.success("Section masquée. Réaffichez-la via « Personnaliser »")).catch(() => {});
  };
  const doneHint = (txt) => editMode && <p className="text-xs text-brand-green/50 px-2 py-3">{txt}</p>;

  // Tri des alertes : les plus urgentes (échues) d'abord, dans chaque section et entre sections.
  const daysOf = (x) => (x.days == null ? 5000 : x.days);
  const bySoon = (arr) => [...(arr || [])].sort((a, b) => daysOf(a) - daysOf(b));
  const rank = (arr) => ((arr || []).length ? Math.min(...arr.map(daysOf)) : 100000);
  // Sections repliables sur mobile (choix mémorisé).
  const foldClick = (e) => {
    if (window.innerWidth >= 1024) return;
    const card = e.target.closest("h3")?.parentElement;
    const k = card?.dataset?.alert;
    if (!k) return;
    const next = folded.includes(k) ? folded.filter((x) => x !== k) : [...folded, k];
    setFolded(next);
    localStorage.setItem("dash_alerts_folded", JSON.stringify(next));
  };

  const renderBlock = (key) => {
    switch (key) {
      case "onboarding":
        return <div data-testid="block-onboarding"><OnboardingChecklist onHide={() => hideBlock("onboarding")} />{doneHint("Visible tant que vos 5 premières étapes ne sont pas terminées.")}</div>;
      case "further":
        return <div data-testid="block-further"><GoFurtherChecklist onHide={() => hideBlock("further")} />{doneHint("Visible après votre premier bail, tant que les 5 modules ne sont pas découverts.")}</div>;
      case "stats":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6" data-testid="block-stats">
            <StatCard icon={Building2} label="Biens gérés" value={stats.properties} testid="stat-properties" />
            <StatCard icon={Users} label="Locataires actifs" value={stats.tenants} testid="stat-tenants" />
            <StatCard icon={Wallet} label="Encaissé ce mois" value={`${Math.round(stats.income || 0).toLocaleString("fr-FR")} €`} testid="stat-income" />
            <StatCard icon={AlertCircle} label="Quittances en attente" value={stats.pending} warn={stats.pending > 0} testid="stat-pending" />
          </div>
        );
      case "assistant":
        return (
          <div className="relative rounded-2xl bg-brand-greenDeep p-6 text-white overflow-hidden" data-testid="block-assistant">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-brand-lime/20 rounded-full blur-2xl" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-brand-lime mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> Nouveau
                </span>
                <h3 className="font-display text-xl font-semibold">Votre assistant IA</h3>
                <p className="text-sm text-white/70 mt-2 max-w-lg">Rédigez des relances, analysez vos finances et générez vos documents en un clic.</p>
              </div>
              <button onClick={() => navigate("/app/assistant-ia")} className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime text-brand-greenDeep font-semibold text-sm hover:bg-brand-limeDark transition-colors">
                Ouvrir l'assistant <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      case "revenue":
        return null;
      case "tasks":
        return <TasksBlock version={tasksVersion} />;
      case "alerts":
        if (!hasAlerts) {
          return editMode
            ? <div className="bg-white rounded-2xl p-6 border border-brand-soft text-sm text-brand-green/50" data-testid="block-alerts">Aucune alerte actuellement. Ce bloc apparaîtra dès qu'un rappel est actif.</div>
            : null;
        }
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 [&>*]:min-w-0" data-testid="alerts-panel" onClick={foldClick}>
            <div data-alert="passoire" data-folded={folded.includes("passoire") ? "1" : undefined} style={{ order: rank(alerts.passoire) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-red-200">
              <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-red-500" /> Passoires thermiques ({alerts.passoire.length})</h3>
              <div className="space-y-2">
                {bySoon(alerts.passoire).map((pr) => (
                  <button key={pr.key} onClick={() => navigate(`/app/biens/${pr.id}`)} onContextMenu={(e) => openCtx(e, { ...pr, kind: "dpe", property_id: pr.id, title: `DPE — ${pr.name} : ${pr.reasons.join(", ")}`, subtitle: `${pr.name} · ${pr.reasons.join(" · ")}` })} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-red-50 transition-colors text-left" data-testid={`alert-passoire-${pr.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-greenDeep truncate">{pr.name}</p>
                      <p className="text-xs text-brand-green/50 truncate">{pr.reasons.join(" · ")}{pr.expiry_date ? ` · fin ${new Date(pr.expiry_date).toLocaleDateString("fr-FR")}` : ""} · source : {pr.source}</p>
                    </div>
                    <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${["F", "G"].includes(pr.energy_class) || (pr.days != null && pr.days < 0) ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{pr.energy_class ? `DPE ${pr.energy_class}` : "DPE"}{pr.days != null && pr.days <= 30 ? (pr.days < 0 ? " · expiré" : ` · ${pr.days} j`) : ""}</span>
                  </button>
                ))}
                {alerts.passoire.length === 0 && <p className="text-sm text-brand-green/50">Aucun bien classé F ou G ni DPE à renouveler. 👍</p>}
                {alerts.passoire.length > 0 && <p className="text-[11px] text-brand-green/40 pt-1">Clic droit sur une ligne pour la transformer en tâche ou la supprimer.</p>}
              </div>
            </div>
            <div data-alert="docs" data-folded={folded.includes("docs") ? "1" : undefined} style={{ order: rank(visibleDocs) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
              <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-amber-500" /> Documents à renouveler ({visibleDocs.length})</h3>
              <div className="space-y-2">
                {bySoon(visibleDocs).map((d) => (
                  <div key={d.key} onClick={() => (d.kind === "assurance" ? navigate("/app/locations") : d.kind?.startsWith("company") ? navigate("/app/locataires") : navigate(`/app/biens/${d.property_id}`))} onContextMenu={(e) => openCtx(e, { ...d, title: d.kind === "assurance" ? `Demander l'attestation d'assurance — ${d.tenant}` : d.kind === "company_piece" ? `Demander : ${d.label}` : `Renouveler : ${d.label}`, subtitle: `${d.category} · ${d.property_name}` })} className="group w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left cursor-pointer" data-testid={`alert-doc-${d.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-greenDeep truncate">{d.label}</p>
                      <p className="text-xs text-brand-green/50 truncate">{d.category} · {d.property_name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${d.days < 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{d.days < 0 ? "Expiré" : `${d.days} j`}</span>
                      <button onClick={(e) => dismissDoc(d, e)} title="Effacer cette alerte" data-testid={`dismiss-doc-${d.id}`} className="text-brand-green/30 hover:text-red-500 transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {visibleDocs.length === 0 && <p className="text-sm text-brand-green/50">Aucun document à renouveler. 👍</p>}
              </div>
            </div>
            {(alerts.expiring_pieces || []).length > 0 && (
              <div data-alert="pieces" data-folded={folded.includes("pieces") ? "1" : undefined} style={{ order: rank(alerts.expiring_pieces) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><IdCard className="w-5 h-5 text-amber-500" /> Pièces d'identité à renouveler ({alerts.expiring_pieces.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.expiring_pieces).map((pc) => (
                    <button key={pc.id} onClick={() => navigate(`/app/locataires/${pc.id}`)} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-piece-${pc.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{pc.name}</p>
                        <p className="text-xs text-brand-green/50 truncate">{pc.piece_type}</p>
                      </div>
                      <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${pc.days < 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{pc.days < 0 ? "Expirée" : `${pc.days} j`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.ending_leases || []).length > 0 && (
              <div data-alert="leases" data-folded={folded.includes("leases") ? "1" : undefined} style={{ order: rank(alerts.ending_leases) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><FileSignature className="w-5 h-5 text-amber-500" /> Baux arrivant à échéance ({alerts.ending_leases.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.ending_leases).map((l) => (
                    <button key={l.id} onClick={() => navigate("/app/locations")} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-lease-${l.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{l.property}</p>
                        <p className="text-xs text-brand-green/50 truncate">{l.tenant || "—"} · Fin {new Date(l.end).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${l.days < 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{l.days < 0 ? "Échu" : `${l.days} j`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.revision_reminders || []).length > 0 && (
              <div data-alert="revisions" data-folded={folded.includes("revisions") ? "1" : undefined} style={{ order: rank(alerts.revision_reminders) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-500" /> Révisions de loyer à proposer ({alerts.revision_reminders.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.revision_reminders).map((r) => (
                    <button key={r.key} onClick={() => navigate("/app/locations")} onContextMenu={(e) => openCtx(e, { ...r, kind: "revision", lease_id: r.id, title: `Proposer la révision du loyer — ${r.tenant || r.property}`, subtitle: `${r.property} · anniversaire ${new Date(r.anniversary).toLocaleDateString("fr-FR")}` })} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-revision-${r.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{r.property}</p>
                        <p className="text-xs text-brand-green/50 truncate">{r.tenant || "—"} · Anniversaire {new Date(r.anniversary).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <span className="shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-700">{r.days === 0 ? "Aujourd'hui" : r.days < 0 ? `Depuis ${-r.days} j` : `${r.days} j`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.ending_loans || []).length > 0 && (
              <div data-alert="loans" data-folded={folded.includes("loans") ? "1" : undefined} style={{ order: rank(alerts.ending_loans) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-amber-500" /> Prêts arrivant à échéance ({alerts.ending_loans.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.ending_loans).map((l) => (
                    <button key={l.id} onClick={() => navigate("/app/finances/prets")} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-loan-ending-${l.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{l.type}{l.payer_to ? ` · ${l.payer_to}` : ""}</p>
                        <p className="text-xs text-brand-green/50 truncate">{l.property || "—"} · Dernière échéance {new Date(l.last_date).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold ${l.days < 0 ? "bg-brand-emerald/10 text-brand-emerald" : "bg-amber-100 text-amber-700"}`}>{l.days < 0 ? "Soldé" : (l.months_left <= 1 ? "Dernière" : `${l.months_left} éch.`)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.loan_insurance_reviews || []).length > 0 && (
              <div data-alert="loanins" data-folded={folded.includes("loanins") ? "1" : undefined} style={{ order: rank(alerts.loan_insurance_reviews) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-amber-500" /> Assurance emprunteur à comparer ({alerts.loan_insurance_reviews.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.loan_insurance_reviews).map((r) => (
                    <button key={r.id} onClick={() => navigate("/app/finances/prets")} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-loan-insurance-${r.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{r.type}{r.payer_to ? ` · ${r.payer_to}` : ""}</p>
                        <p className="text-xs text-brand-green/50 truncate">{r.property || "—"} · {(r.annual_insurance || 0).toLocaleString("fr-FR")} € / an — pensez à renégocier</p>
                      </div>
                      <span className="shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-700">{r.days === 0 ? "Aujourd'hui" : `${r.days} j`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.loan_payment_reminders || []).length > 0 && (
              <div data-alert="loanpay" data-folded={folded.includes("loanpay") ? "1" : undefined} style={{ order: rank(alerts.loan_payment_reminders) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><Wallet className="w-5 h-5 text-amber-500" /> Prélèvements de prêt à venir ({alerts.loan_payment_reminders.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.loan_payment_reminders).map((l) => (
                    <button key={l.id} onClick={() => navigate("/app/finances/prets")} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors text-left" data-testid={`alert-loan-payment-${l.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{l.type}{l.payer_to ? ` · ${l.payer_to}` : ""}</p>
                        <p className="text-xs text-brand-green/50 truncate">{l.property || "—"} · {(l.amount || 0).toLocaleString("fr-FR")} € le {new Date(l.date).toLocaleDateString("fr-FR")} — vérifiez la provision</p>
                      </div>
                      <span className="shrink-0 text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-700">{l.days === 0 ? "Aujourd'hui" : `${l.days} j`}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(alerts.unpaid_deposits || []).length > 0 && (
              <div data-alert="deposits" data-folded={folded.includes("deposits") ? "1" : undefined} style={{ order: rank(alerts.unpaid_deposits) }} className="bg-white rounded-2xl p-4 sm:p-6 border min-w-0 border-amber-200" data-testid="alert-deposits-panel">
                <h3 className="font-semibold text-lg text-brand-greenDeep mb-4 flex items-center gap-2"><PiggyBank className="w-5 h-5 text-amber-500" /> Dépôts de garantie à encaisser ({alerts.unpaid_deposits.length})</h3>
                <div className="space-y-2">
                  {bySoon(alerts.unpaid_deposits).map((d) => (
                    <div key={d.id} className="w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl hover:bg-amber-50 transition-colors" data-testid={`alert-deposit-${d.id}`}>
                      <button onClick={() => navigate("/app/finances")} className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{d.tenant || "—"}{d.property ? ` · ${d.property}` : ""}</p>
                        <p className="text-xs text-brand-green/50 truncate">Dépôt de {(d.amount || 0).toLocaleString("fr-FR")} € en attente{d.date ? ` depuis le ${new Date(d.date).toLocaleDateString("fr-FR")}` : ""}</p>
                      </button>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-amber-100 text-amber-700">{d.days != null ? (d.days <= 0 ? "À venir" : `${d.days} j`) : "En attente"}</span>
                        <button onClick={(e) => markDepositPaid(d.id, e)} disabled={depositBusy === d.id} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep disabled:opacity-60" data-testid={`mark-deposit-paid-${d.id}`}>
                          {depositBusy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Marquer encaissé
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case "properties":
        return (
          <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="block-properties">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-lg text-brand-greenDeep">Aperçu des biens</h3>
              <button onClick={() => navigate("/app/biens")} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-greenDeep hover:gap-2 transition-all">
                Tout voir <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {propertiesPreview.map((p) => (
                <div key={p.id} className="rounded-xl overflow-hidden border border-brand-soft group cursor-pointer" onClick={() => navigate("/app/biens")}>
                  <div className="h-28 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-brand-greenDeep truncate">{p.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-brand-green/50">{p.rent} €/mois</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.status === "Occupé" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-orange-100 text-orange-700"}`}>{p.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {showOnboarding && <OnboardingModal onFinish={finishOnboarding} />}
      <AlertContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} onTask={setTaskAlert} onDismiss={dismissAlert} />
      {taskAlert && <TaskFromAlertModal alert={taskAlert} onClose={() => setTaskAlert(null)} onCreated={onTaskCreated} />}
      <OnboardingBanner />
      <GettingStarted />
      {disputes.count > 0 && (
        <button onClick={() => navigate("/app/edl-historique")} className="w-full text-left bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 flex items-center gap-3 hover:bg-amber-100/70 transition-colors" data-testid="dispute-alert-banner">
          <span className="w-10 h-10 rounded-xl bg-amber-200 text-amber-800 flex items-center justify-center shrink-0"><AlertCircle className="w-5 h-5" /></span>
          <span className="flex-1">
            <span className="block text-sm font-bold text-amber-900" data-testid="dispute-alert-count">{disputes.count} litige{disputes.count > 1 ? "s" : ""} en cours</span>
            <span className="block text-xs text-amber-800/80">{disputes.items.slice(0, 3).map((i) => i.property_name).filter(Boolean).join(", ") || "État(s) des lieux concerné(s)"} — cliquez pour consulter et clôturer</span>
          </span>
          <ArrowRight className="w-4 h-4 text-amber-800" />
        </button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex p-1 rounded-full bg-brand-softer border border-brand-soft" data-testid="dash-view-switch">
          <button onClick={() => setView("dashboard")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "dashboard" ? "bg-brand-greenDeep text-white" : "text-brand-greenDeep"}`} data-testid="dash-view-dashboard">Tableau de bord</button>
          <button onClick={() => setView("ai")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${view === "ai" ? "bg-brand-greenDeep text-white" : "text-brand-greenDeep"}`} data-testid="dash-view-ai"><Sparkles className="w-4 h-4" /> Écran IA</button>
        </div>
        {view === "ai" ? <span /> : editMode ? (
          <>
            <button onClick={() => { setEditMode(false); }} className="px-4 py-2 rounded-full border border-brand-soft text-sm text-brand-green/70 hover:bg-brand-softer transition-colors" data-testid="dash-cancel-btn">Annuler</button>
            <button onClick={savePrefs} disabled={savingPrefs} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="dash-save-btn">
              <Check className="w-4 h-4" /> Terminer
            </button>
          </>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button onClick={openGuidedProperty} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime/30 hover:bg-brand-lime/50 text-sm font-medium text-brand-greenDeep transition-colors" data-testid="dash-guided-property-btn">
              <Plus className="w-4 h-4" /> Ajouter un bien guidé
            </button>
            <button onClick={() => setEditMode(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft text-sm font-medium text-brand-greenDeep hover:bg-brand-softer transition-colors" data-testid="dash-customize-btn">
              <Settings2 className="w-4 h-4" /> Personnaliser
            </button>
          </div>
        )}
      </div>

      {view === "ai" ? <AssistantIA embedded /> : editMode ? (
        <>
          <p className="text-sm text-brand-green/55 bg-brand-lime/10 border border-brand-lime/30 rounded-xl px-4 py-3">Glissez les blocs par la poignée pour les réorganiser, ou masquez/affichez-les. Cliquez sur « Terminer » pour enregistrer.</p>
          <Reorder.Group axis="y" values={order.filter((k) => BLOCK_LABELS[k])} onReorder={(v) => setOrder([...v, ...order.filter((k) => !BLOCK_LABELS[k])])} className="space-y-4">
            {order.filter((k) => BLOCK_LABELS[k]).map((key) => (
              <DraggableBlock key={key} blockKey={key} hidden={hidden.includes(key)} onToggle={() => toggleHidden(key)}>
                {renderBlock(key)}
              </DraggableBlock>
            ))}
          </Reorder.Group>
        </>
      ) : (
        <div className="space-y-6">
          {order.filter((k) => !hidden.includes(k)).map((key) => {
            const block = renderBlock(key);
            return block ? <div key={key} className="has-[>div:empty]:hidden">{block}</div> : null;
          })}
        </div>
      )}
    </div>
  );
}
